import type * as DB from '@sosocio/creator-types/database';

/**
 * Per-label UI styling delivered at runtime from the backend `/start` response
 * (FE-2372). Generic white-label partners run on the default build, so these
 * definitions let each label override the editor chrome (semantic design
 * tokens and UI fonts) without a per-label frontend deployment.
 *
 * Every field is optional. When a field is absent or invalid the editor keeps
 * its build-time brand defaults — the runtime override only ever adds.
 */
export interface BrandStyleDefinitions {
	/**
	 * Semantic design-token overrides applied to the document root, e.g.
	 * `{ '--color-background-brand-primary': '#ff6600', '--font-family-regular': '"AcmeSans"' }`.
	 * The optional `dark` object can override the same variables for dark
	 * mode. Runtime rules are scoped to `:root`, FastEditor's root label,
	 * and legacy `.light`/`.dark` containers so generic labels can style both
	 * semantic and legacy chrome.
	 */
	cssVariables?: Record<string, unknown> & {
		dark?: Record<string, unknown>;
	};
	/**
	 * Runtime logo override for generic-label chrome. `image` may be either a
	 * raw `data:image/...` URL or a CSS `url("data:image/...")` value.
	 */
	logo?: BrandStyleLogoDefinition;
	/**
	 * Runtime browser favicon override for generic labels. This updates the
	 * document head after `/start`; generated web manifests remain build-time.
	 */
	favicon?: BrandStyleFaviconDefinition;
	/**
	 * Runtime document/app metadata for generic labels. This updates the
	 * current document head; external crawlers may still see the build-time
	 * HTML because they usually do not execute the app.
	 */
	metadata?: BrandStyleMetadataDefinition;
	/**
	 * Custom UI fonts to load so any `--font-family-*` override above can
	 * resolve. These drive the editor chrome only — they are not exposed in the
	 * user-selectable content-font picker.
	 */
	fonts?: BrandStyleFont[];
}

export type BrandStyleFont = OptionalExceptFor<DB.FontModel, 'id'>;

export interface BrandStyleLogoProperties {
	image?: string;
	backgroundImage?: string;
	backgroundSize?: string;
	width?: string;
	height?: string;
}

export interface BrandStyleLogoDefinition extends BrandStyleLogoProperties {
	desktop?: BrandStyleLogoProperties;
	mobile?: BrandStyleLogoProperties;
}

export interface BrandStyleFaviconDefinition {
	image?: string;
	sizes?: string;
	appleTouchIcon?: string;
}

export type BrandStyleManifestDisplay = 'browser' | 'fullscreen' | 'minimal-ui' | 'standalone';

export interface BrandStyleManifestDefinition {
	enabled?: boolean;
	title?: string;
	shortTitle?: string;
	description?: string;
	themeColor?: string;
	backgroundColor?: string;
	startUrl?: string;
	display?: BrandStyleManifestDisplay;
}

export interface BrandStyleMetadataDefinition {
	title?: string;
	shortTitle?: string;
	description?: string;
	applicationName?: string;
	appleMobileWebAppTitle?: string;
	appleMobileWebAppStatusBarStyle?: string;
	themeColor?: string;
	backgroundColor?: string;
	msApplicationTileColor?: string;
	msApplicationTileImage?: string;
	msApplicationConfig?: string;
	startUrl?: string;
	display?: BrandStyleManifestDisplay;
	manifest?: boolean | BrandStyleManifestDefinition;
}

export interface ApplyBrandStylesOptions {
	/** Element that receives the CSS custom properties. Defaults to the document root. */
	root?: HTMLElement;
	/**
	 * Loader for the custom fonts. The appdata `/start` handler passes
	 * `FontModule.addModels`; kept injectable so this helper stays free of the
	 * store and is unit-testable on its own.
	 */
	loadFonts?: (fonts: BrandStyleFont[]) => Promise<unknown>;
}

export interface ApplyBrandStylesResult {
	appliedVariables: string[];
	skippedVariables: string[];
	appliedLogoStyles: string[];
	skippedLogoStyles: string[];
	appliedFavicons: string[];
	skippedFavicons: string[];
	appliedMetadata: string[];
	skippedMetadata: string[];
	loadedFonts: string[];
}

/** A CSS custom property name: `--` followed by name characters. */
const CSS_VARIABLE_NAME = /^--[a-z0-9-]+$/i;
const RUNTIME_STYLE_ATTRIBUTE = 'data-runtime-brand-styles';
const BASE_VARIABLE_SELECTORS = [
	':root',
	':root[data-label="fasteditor"]',
	'.light',
	'.dark',
];
const DARK_VARIABLE_SELECTORS = [
	':root[data-label="fasteditor"][data-theme="dark"]',
	'.dark',
];
const BASE_LOGO_SELECTORS = [
	'.logo',
	'.desktopLogo',
	'.mobileLogo',
];
const DESKTOP_LOGO_SELECTORS = ['.desktopLogo'];
const MOBILE_LOGO_SELECTORS = ['.mobileLogo'];
const RESPONSIVE_LOGO_SELECTORS = ['.logo'];
const DESKTOP_MEDIA_QUERY = 'only screen and (min-width: 768px)';
const MOBILE_MEDIA_QUERY = 'only screen and (max-width: 767px)';
const DATA_IMAGE_URL = /^data:image\/(?:svg\+xml|png|jpe?g|gif|webp)(?:;[a-z0-9.+_-]+=[a-z0-9.+_-]+)*(?:;base64)?,[a-z0-9!#$%&'()*+\-.^_`|~:/?=[\]@,]*$/i;
const UNSAFE_DATA_IMAGE_URL = /["'\\<>{}\s]/;
const RUNTIME_FAVICON_ATTRIBUTE = 'data-runtime-brand-favicon';
const RUNTIME_MANIFEST_ATTRIBUTE = 'data-runtime-brand-manifest';
const SAFE_FAVICON_SIZES = /^(?:any|[1-9]\d*x[1-9]\d*(?:\s+[1-9]\d*x[1-9]\d*)*)$/i;
const UNSAFE_METADATA_VALUE = /[<>]/;
const MANIFEST_DISPLAYS: BrandStyleManifestDisplay[] = [
	'browser',
	'fullscreen',
	'minimal-ui',
	'standalone',
];

/**
 * Values that could break out of a declaration or fetch external resources.
 * Brand colors and font-family lists never need these, so rejecting them keeps
 * a malformed or hostile payload from injecting arbitrary CSS.
 */
const UNSAFE_VALUE = /[;{}<>]|url\(|@import|expression\(/i;

/**
 * Checks whether an unknown payload value is a plain object that can be safely
 * inspected as a keyed `/start` response section.
 *
 * @param value Value from the untrusted runtime brand styles payload.
 * @returns Whether the value is a non-array object.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value);
}

/**
 * Validates one CSS custom property before it is emitted into inline styles or
 * the runtime override style tag.
 *
 * @param name CSS custom property name from the payload.
 * @param value CSS custom property value from the payload.
 * @returns Whether the pair is safe to emit as a CSS variable declaration.
 */
function isValidCssVariable(
	name: string,
	value: unknown,
): value is string {
	return (
		CSS_VARIABLE_NAME.test(name)
		&& typeof value === 'string'
		&& value.trim() !== ''
		&& !UNSAFE_VALUE.test(value)
	);
}

/**
 * Serializes validated CSS custom properties into declarations that intentionally
 * override build-time label CSS.
 *
 * @param variables Validated CSS variable tuples.
 * @returns CSS declaration lines for a style block.
 */
function declarationsFromVariables(variables: [string, string][]): string {
	return variables
		.map(([
			name,
			value,
		]) => `\t${name}: ${value} !important;`)
		.join('\n');
}

/**
 * Serializes validated regular CSS properties into declarations for runtime
 * logo overrides.
 *
 * @param properties Validated CSS property tuples.
 * @returns CSS declaration lines for a style block.
 */
function declarationsFromProperties(properties: [string, string][]): string {
	return properties
		.map(([
			name,
			value,
		]) => `\t${name}: ${value} !important;`)
		.join('\n');
}

/**
 * Builds a runtime CSS block for one selector group and a set of validated
 * regular properties.
 *
 * @param selectors Selectors that should receive the same declarations.
 * @param properties Validated CSS property tuples.
 * @returns Complete CSS block text.
 */
function styleBlockFromProperties(
	selectors: string[],
	properties: [string, string][],
): string {
	return `${selectors.join(',\n')} {\n${declarationsFromProperties(properties)}\n}`;
}

/**
 * Wraps a regular runtime CSS block in a media query so generic logo sizing can
 * mimic the existing desktop/mobile label split.
 *
 * @param mediaQuery Media query condition without the `@media` prefix.
 * @param selectors Selectors scoped by the media query.
 * @param properties Validated CSS property tuples.
 * @returns Complete media-query CSS block text.
 */
function mediaStyleBlockFromProperties(
	mediaQuery: string,
	selectors: string[],
	properties: [string, string][],
): string {
	return `@media ${mediaQuery} {\n${styleBlockFromProperties(
		selectors,
		properties,
	)}\n}`;
}

/**
 * Finds the shared runtime style tag or creates it in the same document as the
 * provided root element.
 *
 * @param root Element whose owner document should receive the style tag.
 * @returns The runtime style tag used for brand overrides.
 */
function findOrCreateRuntimeStyle(root: HTMLElement): HTMLStyleElement {
	const doc = root.ownerDocument;
	const existingStyle = doc.head.querySelector<HTMLStyleElement>(
		`style[${RUNTIME_STYLE_ATTRIBUTE}]`,
	);

	if (existingStyle) {
		return existingStyle;
	}

	const style = doc.createElement('style');

	style.setAttribute(
		RUNTIME_STYLE_ATTRIBUTE,
		'',
	);
	doc.head.append(style);

	return style;
}

/**
 * Replaces the runtime brand style tag with the current CSS blocks, removing it
 * entirely when no runtime CSS is needed.
 *
 * @param root Element whose owner document contains the runtime style tag.
 * @param styleBlocks Complete CSS blocks to emit.
 */
function applyRuntimeStyles(
	root: HTMLElement,
	styleBlocks: string[],
): void {
	const doc = root.ownerDocument;

	if (styleBlocks.length === 0) {
		doc.head
			.querySelector<HTMLStyleElement>(`style[${RUNTIME_STYLE_ATTRIBUTE}]`)
			?.remove();

		return;
	}

	const style = findOrCreateRuntimeStyle(root);

	style.textContent = styleBlocks.join('\n\n');
}

/**
 * Creates the scoped CSS blocks for base and dark-mode custom properties,
 * including legacy `.light` and `.dark` containers.
 *
 * @param baseVariables CSS variables that apply to all themes.
 * @param darkVariables CSS variables that apply only to dark mode scopes.
 * @returns CSS blocks for the runtime style tag.
 */
function getScopedCssVariableBlocks(
	baseVariables: [string, string][],
	darkVariables: [string, string][],
): string[] {
	const styleBlocks: string[] = [];

	if (baseVariables.length > 0) {
		styleBlocks.push(`${BASE_VARIABLE_SELECTORS.join(',\n')} {\n${declarationsFromVariables(baseVariables)}\n}`);
	}

	if (darkVariables.length > 0) {
		styleBlocks.push(`${DARK_VARIABLE_SELECTORS.join(',\n')} {\n${declarationsFromVariables(darkVariables)}\n}`);
	}

	return styleBlocks;
}

/**
 * Extracts valid base and dark CSS variables from the untrusted payload while
 * recording accepted and rejected entries in the result object.
 *
 * @param cssVariables Raw `cssVariables` payload.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns Valid CSS variables split by base and dark scopes.
 */
function collectCssVariables(
	cssVariables: unknown,
	result: ApplyBrandStylesResult,
): {
	baseVariables: [string, string][];
	darkVariables: [string, string][];
} {
	const baseVariables: [string, string][] = [];
	const darkVariables: [string, string][] = [];

	if (!isPlainObject(cssVariables)) {
		return {
			baseVariables,
			darkVariables,
		};
	}

	Object
		.entries(cssVariables)
		.forEach(([
			name,
			value,
		]) => {
			if (name === 'dark') {
				if (isPlainObject(value)) {
					Object
						.entries(value)
						.forEach(([
							darkName,
							darkValue,
						]) => {
							if (
								isValidCssVariable(
									darkName,
									darkValue,
								)
							) {
								darkVariables.push([
									darkName,
									darkValue,
								]);
								result.appliedVariables.push(`dark.${darkName}`);
							} else {
								result.skippedVariables.push(`dark.${darkName}`);
							}
						});
				}

				return;
			}

			if (
				isValidCssVariable(
					name,
					value,
				)
			) {
				baseVariables.push([
					name,
					value,
				]);
				result.appliedVariables.push(name);
			} else {
				result.skippedVariables.push(name);
			}
		});

	return {
		baseVariables,
		darkVariables,
	};
}

/**
 * Applies root-level inline custom properties for immediate availability while
 * avoiding inline values that would block dark-mode selector overrides.
 *
 * @param root Element that receives inline CSS variables.
 * @param baseVariables Valid base CSS variable tuples.
 * @param darkVariables Valid dark-mode CSS variable tuples.
 */
function applyInlineCssVariables(
	root: HTMLElement,
	baseVariables: [string, string][],
	darkVariables: [string, string][],
): void {
	const darkVariableNames = new Set(darkVariables.map((variable) => variable[0]));
	baseVariables.forEach(([
		name,
		value,
	]) => {
		if (darkVariableNames.has(name)) {
			root.style.removeProperty(name);

			return;
		}

		root.style.setProperty(
			name,
			value,
			'important',
		);
	});
}

/**
 * Applies inline CSS variables and returns the matching scoped style blocks for
 * the runtime style tag.
 *
 * @param cssVariables Raw `cssVariables` payload.
 * @param root Element that receives inline variables.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns CSS blocks that should be emitted to the runtime style tag.
 */
function applyCssVariables(
	cssVariables: unknown,
	root: HTMLElement,
	result: ApplyBrandStylesResult,
): string[] {
	const {
		baseVariables,
		darkVariables,
	} = collectCssVariables(
		cssVariables,
		result,
	);
	applyInlineCssVariables(
		root,
		baseVariables,
		darkVariables,
	);

	return getScopedCssVariableBlocks(
		baseVariables,
		darkVariables,
	);
}

/**
 * Validates a logo layout property such as width, height, or background-size
 * before it is emitted as regular CSS.
 *
 * @param value Logo property value from the payload.
 * @returns Whether the value is safe for a CSS declaration.
 */
function isSafeLogoPropertyValue(value: unknown): value is string {
	return (
		typeof value === 'string'
		&& value.trim() !== ''
		&& !UNSAFE_VALUE.test(value)
	);
}

/**
 * Normalizes either a raw data image URI or a CSS `url(...)` wrapper down to
 * the embedded URI.
 *
 * @param value Candidate data image string.
 * @returns The extracted URI-like value.
 */
function extractDataImageUrl(value: string): string {
	const trimmedValue = value.trim();
	const urlMatch = trimmedValue.match(/^url\((["']?)(.+)\1\)$/i);

	return (
		urlMatch?.[2]
		?? trimmedValue
	).trim();
}

/**
 * Validates that a payload value is an embedded image URI supported by the
 * runtime chrome overrides.
 *
 * @param value Candidate image value from the payload.
 * @returns The safe data image URI, or `null` when rejected.
 */
function getDataImageUrl(value: unknown): string | null {
	if (typeof value !== 'string') {
		return null;
	}

	const dataImageUrl = extractDataImageUrl(value);

	if (
		!DATA_IMAGE_URL.test(dataImageUrl)
		|| UNSAFE_DATA_IMAGE_URL.test(dataImageUrl)
	) {
		return null;
	}

	return dataImageUrl;
}

/**
 * Converts a safe embedded logo image into a CSS background-image declaration
 * value.
 *
 * @param value Raw logo image value from the payload.
 * @returns CSS `url(...)` value, or `null` when the image is invalid.
 */
function getLogoBackgroundImage(value: unknown): string | null {
	const dataImageUrl = getDataImageUrl(value);

	if (!dataImageUrl) {
		return null;
	}

	return `url("${dataImageUrl}")`;
}

/**
 * Collects valid CSS properties for one logo configuration object and records
 * any rejected entries with the provided result path.
 *
 * @param logo Raw logo object for the base, desktop, or mobile scope.
 * @param path Diagnostic path prefix used in {@link ApplyBrandStylesResult}.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns CSS property tuples for the runtime logo style block.
 */
function collectLogoProperties(
	logo: unknown,
	path: string,
	result: ApplyBrandStylesResult,
): [string, string][] {
	const properties: [string, string][] = [];

	if (!isPlainObject(logo)) {
		return properties;
	}

	const backgroundImage = logo.backgroundImage ?? logo.image;

	if (typeof backgroundImage !== 'undefined') {
		const logoBackgroundImage = getLogoBackgroundImage(backgroundImage);

		if (logoBackgroundImage) {
			properties.push([
				'background-image',
				logoBackgroundImage,
			]);
			result.appliedLogoStyles.push(`${path}.backgroundImage`);
		} else {
			result.skippedLogoStyles.push(`${path}.backgroundImage`);
		}
	}

	([
		[
			'backgroundSize',
			'background-size',
		],
		[
			'width',
			'width',
		],
		[
			'height',
			'height',
		],
	] as const).forEach(([
		sourceProperty,
		cssProperty,
	]) => {
		const value = logo[sourceProperty];

		if (typeof value === 'undefined') {
			return;
		}

		if (isSafeLogoPropertyValue(value)) {
			properties.push([
				cssProperty,
				value,
			]);
			result.appliedLogoStyles.push(`${path}.${sourceProperty}`);
		} else {
			result.skippedLogoStyles.push(`${path}.${sourceProperty}`);
		}
	});

	return properties;
}

/**
 * Builds all runtime CSS blocks needed for generic-label logo image and
 * responsive sizing overrides.
 *
 * @param logo Raw `logo` payload.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns CSS blocks for the runtime style tag.
 */
function collectLogoStyleBlocks(
	logo: unknown,
	result: ApplyBrandStylesResult,
): string[] {
	if (!isPlainObject(logo)) {
		return [];
	}

	const styleBlocks: string[] = [];
	const baseProperties = collectLogoProperties(
		logo,
		'logo',
		result,
	);
	const desktopProperties = collectLogoProperties(
		logo.desktop,
		'logo.desktop',
		result,
	);
	const mobileProperties = collectLogoProperties(
		logo.mobile,
		'logo.mobile',
		result,
	);

	if (baseProperties.length > 0) {
		styleBlocks.push(styleBlockFromProperties(
			BASE_LOGO_SELECTORS,
			baseProperties,
		));
	}

	if (desktopProperties.length > 0) {
		styleBlocks.push(styleBlockFromProperties(
			DESKTOP_LOGO_SELECTORS,
			desktopProperties,
		));
		styleBlocks.push(mediaStyleBlockFromProperties(
			DESKTOP_MEDIA_QUERY,
			RESPONSIVE_LOGO_SELECTORS,
			desktopProperties,
		));
	}

	if (mobileProperties.length > 0) {
		styleBlocks.push(styleBlockFromProperties(
			MOBILE_LOGO_SELECTORS,
			mobileProperties,
		));
		styleBlocks.push(mediaStyleBlockFromProperties(
			MOBILE_MEDIA_QUERY,
			RESPONSIVE_LOGO_SELECTORS,
			mobileProperties,
		));
	}

	return styleBlocks;
}

/**
 * Infers the MIME type from a validated data image URI so generated link and
 * manifest entries can expose a proper `type` value.
 *
 * @param dataImageUrl Validated data image URI.
 * @returns Lowercase MIME type, or `null` when it cannot be inferred.
 */
function inferDataImageType(dataImageUrl: string): string | null {
	return (
		dataImageUrl
			.match(/^data:([^;,]+)/i)?.[1]
			?.toLowerCase()
		?? null
	);
}

/**
 * Removes build-time favicon links before inserting runtime generic-label
 * favicon links.
 *
 * @param doc Document whose favicon links should be replaced.
 */
function removeExistingFaviconLinks(doc: Document): void {
	doc.head
		.querySelectorAll<HTMLLinkElement>('link[rel]')
		.forEach((link) => {
			const rel = link.rel.trim().toLowerCase();

			if (
				rel === 'icon'
				|| rel === 'shortcut icon'
				|| rel === 'apple-touch-icon'
				|| link.hasAttribute(RUNTIME_FAVICON_ATTRIBUTE)
			) {
				link.remove();
			}
		});
}

/**
 * Appends one runtime favicon link to the document head.
 *
 * @param doc Document that receives the link.
 * @param rel Link relation, for example `icon` or `apple-touch-icon`.
 * @param href Validated embedded image URI.
 * @param sizes Optional favicon sizes attribute.
 */
function appendFaviconLink(
	doc: Document,
	rel: string,
	href: string,
	sizes?: string,
): void {
	const link = doc.createElement('link');
	const type = inferDataImageType(href);

	link.rel = rel;
	link.href = href;
	link.setAttribute(
		RUNTIME_FAVICON_ATTRIBUTE,
		rel,
	);

	if (type) {
		link.type = type;
	}

	if (sizes) {
		link.setAttribute(
			'sizes',
			sizes,
		);
	}

	doc.head.append(link);
}

/**
 * Applies runtime favicon overrides by replacing build-time icon links with
 * embedded image links from the payload.
 *
 * @param favicon Raw `favicon` payload.
 * @param root Element whose owner document receives the favicon links.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 */
function applyFavicon(
	favicon: unknown,
	root: HTMLElement,
	result: ApplyBrandStylesResult,
): void {
	if (!isPlainObject(favicon)) {
		return;
	}

	const faviconImage = getDataImageUrl(favicon.image);

	if (!faviconImage) {
		if (typeof favicon.image !== 'undefined') {
			result.skippedFavicons.push('favicon.image');
		}

		return;
	}

	const faviconImageType = inferDataImageType(faviconImage);
	let sizes: string | undefined;

	if (typeof favicon.sizes === 'string') {
		if (SAFE_FAVICON_SIZES.test(favicon.sizes)) {
			sizes = favicon.sizes;
		} else {
			result.skippedFavicons.push('favicon.sizes');
		}
	} else if (faviconImageType === 'image/svg+xml') {
		sizes = 'any';
	}

	const appleTouchIcon = getDataImageUrl(favicon.appleTouchIcon);
	const doc = root.ownerDocument;

	removeExistingFaviconLinks(doc);
	appendFaviconLink(
		doc,
		'icon',
		faviconImage,
		sizes,
	);
	result.appliedFavicons.push('favicon.image');

	if (appleTouchIcon) {
		appendFaviconLink(
			doc,
			'apple-touch-icon',
			appleTouchIcon,
		);
		result.appliedFavicons.push('favicon.appleTouchIcon');
	} else if (typeof favicon.appleTouchIcon !== 'undefined') {
		result.skippedFavicons.push('favicon.appleTouchIcon');
	}
}

/**
 * Validates a document metadata value before it is written into title, meta, or
 * manifest output.
 *
 * @param value Candidate metadata string from the payload.
 * @returns Trimmed metadata value, or `null` when rejected.
 */
function getSafeMetadataValue(value: unknown): string | null {
	if (
		typeof value !== 'string'
		|| value.trim() === ''
		|| UNSAFE_METADATA_VALUE.test(value)
		|| Array
			.from(value)
			.some((character) => {
				const characterCode = character.charCodeAt(0);

				return (
					characterCode < 32
					|| characterCode === 127
				);
			})
	) {
		return null;
	}

	return value.trim();
}

/**
 * Reads one safe metadata field and records a skipped diagnostic when a provided
 * value is invalid.
 *
 * @param metadata Raw metadata object.
 * @param field Field name to read.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns Safe field value, or `null` when missing or invalid.
 */
function getMetadataField(
	metadata: Record<string, unknown>,
	field: string,
	result: ApplyBrandStylesResult,
): string | null {
	const value = getSafeMetadataValue(metadata[field]);

	if (value) {
		return value;
	}

	if (typeof metadata[field] !== 'undefined') {
		result.skippedMetadata.push(`metadata.${field}`);
	}

	return null;
}

/**
 * Finds or creates a meta tag keyed by `name` or `property`.
 *
 * @param doc Document whose head should contain the meta tag.
 * @param attribute Attribute used to identify the tag.
 * @param key Attribute value used to identify the tag.
 * @returns Existing or newly created meta element.
 */
function findOrCreateMeta(
	doc: Document,
	attribute: 'name' | 'property',
	key: string,
): HTMLMetaElement {
	const existingMeta = Array
		.from(doc.head.querySelectorAll<HTMLMetaElement>('meta'))
		.find((meta) => meta.getAttribute(attribute) === key);

	if (existingMeta) {
		return existingMeta;
	}

	const meta = doc.createElement('meta');

	meta.setAttribute(
		attribute,
		key,
	);
	doc.head.append(meta);

	return meta;
}

/**
 * Writes a meta content value, creating the target meta tag when needed.
 *
 * @param doc Document whose head should contain the meta tag.
 * @param attribute Attribute used to identify the tag.
 * @param key Attribute value used to identify the tag.
 * @param value Content value to write.
 */
function setMetaContent(
	doc: Document,
	attribute: 'name' | 'property',
	key: string,
	value: string,
): void {
	findOrCreateMeta(
		doc,
		attribute,
		key,
	).setAttribute(
		'content',
		value,
	);
}

/**
 * Removes build-time manifest links before inserting a runtime generated
 * manifest link.
 *
 * @param doc Document whose manifest link should be replaced.
 */
function removeExistingManifestLinks(doc: Document): void {
	doc.head
		.querySelectorAll<HTMLLinkElement>('link[rel]')
		.forEach((link) => {
			if (
				link.rel.trim().toLowerCase() === 'manifest'
				|| link.hasAttribute(RUNTIME_MANIFEST_ATTRIBUTE)
			) {
				link.remove();
			}
		});
}

/**
 * Resolves whether the metadata payload requests a runtime manifest and returns
 * any manifest-specific override values.
 *
 * @param metadata Raw metadata object.
 * @returns Manifest override object, or `null` when runtime manifest generation is disabled.
 */
function getManifestDefinition(
	metadata: Record<string, unknown>,
): Record<string, unknown> | null {
	if (metadata.manifest === true) {
		return {};
	}

	if (
		isPlainObject(metadata.manifest)
		&& metadata.manifest.enabled !== false
	) {
		return metadata.manifest;
	}

	return null;
}

/**
 * Resolves a valid manifest display mode from manifest-specific metadata first,
 * then the broader metadata fallback.
 *
 * @param metadata Raw metadata object.
 * @param manifest Runtime manifest override object.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 * @returns Valid manifest display mode, or `undefined` when absent or rejected.
 */
function getManifestDisplay(
	metadata: Record<string, unknown>,
	manifest: Record<string, unknown>,
	result: ApplyBrandStylesResult,
): BrandStyleManifestDisplay | undefined {
	const display = manifest.display ?? metadata.display;

	if (typeof display === 'undefined') {
		return undefined;
	}

	if (
		typeof display === 'string'
		&& MANIFEST_DISPLAYS.includes(display as BrandStyleManifestDisplay)
	) {
		return display as BrandStyleManifestDisplay;
	}

	result.skippedMetadata.push('metadata.manifest.display');

	return undefined;
}

/**
 * Converts the runtime favicon payload into a web manifest icon entry.
 *
 * @param favicon Raw `favicon` payload.
 * @returns Manifest icon object, or `null` when no valid favicon image exists.
 */
function getManifestIcon(
	favicon: unknown,
): {
	src: string;
	sizes?: string;
	type?: string;
} | null {
	if (!isPlainObject(favicon)) {
		return null;
	}

	const src = getDataImageUrl(favicon.image);

	if (!src) {
		return null;
	}

	const type = (
		inferDataImageType(src)
		?? undefined
	);
	let sizes: string | undefined;

	if (
		typeof favicon.sizes === 'string'
		&& SAFE_FAVICON_SIZES.test(favicon.sizes)
	) {
		sizes = favicon.sizes;
	} else if (type === 'image/svg+xml') {
		sizes = 'any';
	}

	return {
		src,
		sizes,
		type,
	};
}

/**
 * Replaces the build-time manifest link with a generated data-URI manifest that
 * uses runtime metadata and favicon values.
 *
 * @param metadata Raw metadata object.
 * @param favicon Raw `favicon` payload reused as the manifest icon.
 * @param root Element whose owner document receives the manifest link.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 */
function applyRuntimeManifest(
	metadata: Record<string, unknown>,
	favicon: unknown,
	root: HTMLElement,
	result: ApplyBrandStylesResult,
): void {
	const manifestDefinition = getManifestDefinition(metadata);

	if (!manifestDefinition) {
		return;
	}

	const title = (
		getSafeMetadataValue(manifestDefinition.title)
		?? getSafeMetadataValue(metadata.title)
	);
	const shortTitle = (
		getSafeMetadataValue(manifestDefinition.shortTitle)
		?? getSafeMetadataValue(metadata.shortTitle)
	);
	const description = (
		getSafeMetadataValue(manifestDefinition.description)
		?? getSafeMetadataValue(metadata.description)
	);
	const themeColor = (
		getSafeMetadataValue(manifestDefinition.themeColor)
		?? getSafeMetadataValue(metadata.themeColor)
	);
	const backgroundColor = (
		getSafeMetadataValue(manifestDefinition.backgroundColor)
		?? getSafeMetadataValue(metadata.backgroundColor)
	);
	const startUrl = (
		getSafeMetadataValue(manifestDefinition.startUrl)
		?? getSafeMetadataValue(metadata.startUrl)
	);
	const display = getManifestDisplay(
		metadata,
		manifestDefinition,
		result,
	);
	const icon = getManifestIcon(favicon);
	const manifest: Record<string, unknown> = {};

	if (title) {
		manifest.name = title;
	}

	if (shortTitle ?? title) {
		manifest.short_name = shortTitle ?? title;
	}

	if (description) {
		manifest.description = description;
	}

	if (themeColor) {
		manifest.theme_color = themeColor;
	}

	if (backgroundColor) {
		manifest.background_color = backgroundColor;
	}

	if (startUrl) {
		manifest.start_url = startUrl;
	}

	if (display) {
		manifest.display = display;
	}

	if (icon) {
		manifest.icons = [icon];
	}

	const link = root.ownerDocument.createElement('link');

	link.rel = 'manifest';
	link.href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
	link.setAttribute(
		RUNTIME_MANIFEST_ATTRIBUTE,
		'',
	);
	removeExistingManifestLinks(root.ownerDocument);
	root.ownerDocument.head.append(link);
	result.appliedMetadata.push('metadata.manifest');
}

/**
 * Applies runtime document metadata such as title, Open Graph title, app names,
 * theme color, Microsoft tile metadata, and optional manifest replacement.
 *
 * @param metadata Raw `metadata` payload.
 * @param favicon Raw `favicon` payload reused by manifest generation.
 * @param root Element whose owner document receives metadata updates.
 * @param result Mutable {@link ApplyBrandStylesResult} receiving diagnostics.
 */
function applyMetadata(
	metadata: unknown,
	favicon: unknown,
	root: HTMLElement,
	result: ApplyBrandStylesResult,
): void {
	if (!isPlainObject(metadata)) {
		return;
	}

	const doc = root.ownerDocument;
	const title = getMetadataField(
		metadata,
		'title',
		result,
	);
	const shortTitle = getMetadataField(
		metadata,
		'shortTitle',
		result,
	);
	const description = getMetadataField(
		metadata,
		'description',
		result,
	);
	const applicationName = getMetadataField(
		metadata,
		'applicationName',
		result,
	) ?? title;
	const appleMobileWebAppTitle = getMetadataField(
		metadata,
		'appleMobileWebAppTitle',
		result,
	) ?? shortTitle ?? applicationName;
	const appleMobileWebAppStatusBarStyle = getMetadataField(
		metadata,
		'appleMobileWebAppStatusBarStyle',
		result,
	);
	const themeColor = getMetadataField(
		metadata,
		'themeColor',
		result,
	);
	const msApplicationTileColor = getMetadataField(
		metadata,
		'msApplicationTileColor',
		result,
	) ?? themeColor;
	const msApplicationConfig = getMetadataField(
		metadata,
		'msApplicationConfig',
		result,
	) ?? 'none';
	const msApplicationTileImage = getDataImageUrl(metadata.msApplicationTileImage);

	if (title) {
		doc.title = title;
		setMetaContent(
			doc,
			'property',
			'og:title',
			title,
		);
		result.appliedMetadata.push('metadata.title');
	}

	if (description) {
		setMetaContent(
			doc,
			'name',
			'description',
			description,
		);
		setMetaContent(
			doc,
			'property',
			'og:description',
			description,
		);
		result.appliedMetadata.push('metadata.description');
	}

	if (applicationName) {
		setMetaContent(
			doc,
			'name',
			'application-name',
			applicationName,
		);
		result.appliedMetadata.push('metadata.applicationName');
	}

	if (appleMobileWebAppTitle) {
		setMetaContent(
			doc,
			'name',
			'apple-mobile-web-app-title',
			appleMobileWebAppTitle,
		);
		result.appliedMetadata.push('metadata.appleMobileWebAppTitle');
	}

	if (appleMobileWebAppStatusBarStyle) {
		setMetaContent(
			doc,
			'name',
			'apple-mobile-web-app-status-bar-style',
			appleMobileWebAppStatusBarStyle,
		);
		result.appliedMetadata.push('metadata.appleMobileWebAppStatusBarStyle');
	}

	if (themeColor) {
		setMetaContent(
			doc,
			'name',
			'theme-color',
			themeColor,
		);
		result.appliedMetadata.push('metadata.themeColor');
	}

	if (msApplicationTileColor) {
		setMetaContent(
			doc,
			'name',
			'msapplication-TileColor',
			msApplicationTileColor,
		);
		result.appliedMetadata.push('metadata.msApplicationTileColor');
	}

	if (msApplicationTileImage) {
		setMetaContent(
			doc,
			'name',
			'msapplication-TileImage',
			msApplicationTileImage,
		);
		result.appliedMetadata.push('metadata.msApplicationTileImage');
	} else if (typeof metadata.msApplicationTileImage !== 'undefined') {
		result.skippedMetadata.push('metadata.msApplicationTileImage');
	}

	if (msApplicationConfig) {
		setMetaContent(
			doc,
			'name',
			'msapplication-config',
			msApplicationConfig,
		);
		result.appliedMetadata.push('metadata.msApplicationConfig');
	}

	applyRuntimeManifest(
		metadata,
		favicon,
		root,
		result,
	);
}

/**
 * Checks whether a value is a string with non-whitespace content.
 *
 * @param value Value from the untrusted `/start` payload.
 * @returns Whether the value is a usable string.
 */
function isNonEmptyString(value: unknown): value is string {
	return (
		typeof value === 'string'
		&& value.trim() !== ''
	);
}

/**
 * A font is only loadable if it has a source {@link FontModule} can build a WebFont
 * config from: a custom `url`, `provider: 'google'`, or `provider: 'typekit'`
 * with a `kitID`. Anything else would reach `WebFont.load` with no source and
 * stall, so skip it (the `/start` payload is optional/untrusted — FE-2372 AC5).
 *
 * @param font Font candidate from the untrusted `/start` payload.
 * @returns Whether the font has enough source data for {@link FontModule}.
 */
function hasUsableSource(font: Record<string, unknown>): boolean {
	return (
		isNonEmptyString(font.url)
		|| font.provider === 'google'
		|| (
			font.provider === 'typekit'
			&& isNonEmptyString(font.kitID)
		)
	);
}

/**
 * Filters and normalizes runtime brand fonts so they load for editor chrome
 * only and are never added to the user-selectable print font picker.
 *
 * @param fonts Raw `fonts` payload.
 * @returns Loadable {@link BrandStyleFont} entries for {@link FontModule}.
 */
function collectFonts(fonts: unknown): BrandStyleFont[] {
	if (!Array.isArray(fonts)) {
		return [];
	}

	return fonts
		.filter((font): font is BrandStyleFont => isPlainObject(font)
			&& isNonEmptyString(font.id)
			&& hasUsableSource(font))
		.map((font) => ({
			...font,
			/*
			 * Chrome typography only (FE-2372): load immediately so the
			 * `--font-family-*` overrides resolve, and keep the brand UI font
			 * out of the user-selectable content-font picker (`print: false`).
			 */
			autoload: true,
			print: false,
		}));
}

/**
 * Apply backend-provided brand styling to the editor chrome at runtime.
 *
 * CSS variables are applied synchronously; fonts load asynchronously and never
 * block bootstrap. Invalid entries are skipped rather than thrown so the editor
 * stays usable on incomplete or malformed payloads.
 *
 * @param brandStyles Runtime brand-style payload from the backend `/start` response.
 * @param options Optional root element and font loader hooks.
 * @returns Diagnostics describing applied and skipped runtime brand style entries.
 */
export function applyBrandStyles(
	brandStyles: BrandStyleDefinitions | null | undefined,
	options: ApplyBrandStylesOptions = {},
): ApplyBrandStylesResult {
	const result: ApplyBrandStylesResult = {
		appliedVariables: [],
		skippedVariables: [],
		appliedLogoStyles: [],
		skippedLogoStyles: [],
		appliedFavicons: [],
		skippedFavicons: [],
		appliedMetadata: [],
		skippedMetadata: [],
		loadedFonts: [],
	};

	if (!isPlainObject(brandStyles)) {
		return result;
	}

	const root = options.root ?? document.documentElement;
	const styleBlocks = [
		...applyCssVariables(
			brandStyles.cssVariables,
			root,
			result,
		),
		...collectLogoStyleBlocks(
			brandStyles.logo,
			result,
		),
	];

	applyRuntimeStyles(
		root,
		styleBlocks,
	);
	applyFavicon(
		brandStyles.favicon,
		root,
		result,
	);
	applyMetadata(
		brandStyles.metadata,
		brandStyles.favicon,
		root,
		result,
	);

	const fonts = collectFonts(brandStyles.fonts);

	if (fonts.length > 0 && options.loadFonts) {
		result.loadedFonts = fonts.map((font) => font.id);

		options.loadFonts(fonts).catch((error: unknown) => {
			/**
			 * Keep the editor usable if a brand font fails to load (FE-2372).
			 */
			console.warn(
				'[brand-styles] failed to load custom brand fonts',
				error,
			);
		});
	}

	return result;
}
